import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import './App.css'
import { ImportSheet } from './components/ImportSheet'
import { MarkdownSurface } from './components/MarkdownSurface'
import { PrivacyNotice } from './components/PrivacyNotice'
import { SidebarPanel } from './components/SidebarPanel'
import { db, getSetting, setSetting } from './db'
import { deriveDocumentName, isMarkdownFile, normalizeMarkdown } from './lib/documents'
import { sha256 } from './lib/hash'
import type {
  DocumentRecord,
  SidebarTab,
  ScreenshotRecord,
  ThemeMode,
  ViewMode,
} from './types'

interface DeferredInstallPrompt extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
}

function HistoryIcon() {
  return (
    <svg viewBox="0 0 24 24" className="toolbar-icon" aria-hidden="true">
      <path
        d="M12 7v5l3 2M4 12a8 8 0 1 0 2.34-5.66M4 4v4h4"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  )
}

function InstallIcon() {
  return (
    <svg viewBox="0 0 24 24" className="toolbar-icon" aria-hidden="true">
      <path
        d="M12 3v10m0 0 4-4m-4 4-4-4M5 17.5h14"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  )
}

function isIOSDevice() {
  const userAgent = navigator.userAgent
  return /iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function isSafariBrowser() {
  const userAgent = navigator.userAgent
  return /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS|OPT\//i.test(userAgent)
}

function isStandaloneDisplayMode() {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigatorWithStandalone.standalone === true
  )
}

function App() {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const readerViewportRef = useRef<HTMLElement | null>(null)
  const latestDocumentRef = useRef<DocumentRecord | null>(null)
  const lastPersistedRef = useRef<{ id: string; markdown: string; name: string } | null>(null)

  const [captureTargetId] = useState(() => `reader-capture-${crypto.randomUUID()}`)
  const [isBooting, setIsBooting] = useState(true)
  const [theme, setTheme] = useState<ThemeMode>('light')
  const [viewMode, setViewMode] = useState<ViewMode>('read')
  const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTab>('saved')
  const [panelOpen, setPanelOpen] = useState(false)
  const [activeDocument, setActiveDocument] = useState<DocumentRecord | null>(null)
  const [installPromptEvent, setInstallPromptEvent] = useState<DeferredInstallPrompt | null>(null)
  const [installHelpOpen, setInstallHelpOpen] = useState(false)
  const [isIOS] = useState(() => isIOSDevice())
  const [isSafari] = useState(() => isSafariBrowser())
  const [isInstalled, setIsInstalled] = useState(false)
  const [privacyOpen, setPrivacyOpen] = useState(false)
  const [importSheetOpen, setImportSheetOpen] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [selectedScreenshot, setSelectedScreenshot] = useState<ScreenshotRecord | null>(null)
  const [isCapturing, setIsCapturing] = useState(false)

  const selectedScreenshotUrl = useMemo(() => {
    return selectedScreenshot ? URL.createObjectURL(selectedScreenshot.blob) : null
  }, [selectedScreenshot])

  const screenshots =
    useLiveQuery(async () => {
      if (!activeDocument) {
        return []
      }

      const items = await db.screenshots
        .where('documentId')
        .equals(activeDocument.id)
        .sortBy('createdAt')
      return items.reverse()
    }, [activeDocument?.id]) ?? []

  const historyDocuments =
    useLiveQuery(async () => {
      return db.documents.orderBy('updatedAt').reverse().toArray()
    }, []) ?? []

  useEffect(() => {
    document.documentElement.dataset.theme = theme
  }, [theme])

  useEffect(() => {
    const displayModeMedia = window.matchMedia('(display-mode: standalone)')
    const updateInstalledState = () => {
      setIsInstalled(isStandaloneDisplayMode())
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      const promptEvent = event as DeferredInstallPrompt
      promptEvent.preventDefault()
      setInstallPromptEvent(promptEvent)
    }

    const handleAppInstalled = () => {
      setInstallPromptEvent(null)
      setInstallHelpOpen(false)
      updateInstalledState()
      setStatusMessage('MarkView installed.')
    }

    updateInstalledState()
    displayModeMedia.addEventListener('change', updateInstalledState)
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener)
    window.addEventListener('appinstalled', handleAppInstalled)
    window.addEventListener('focus', updateInstalledState)
    document.addEventListener('visibilitychange', updateInstalledState)

    return () => {
      displayModeMedia.removeEventListener('change', updateInstalledState)
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener)
      window.removeEventListener('appinstalled', handleAppInstalled)
      window.removeEventListener('focus', updateInstalledState)
      document.removeEventListener('visibilitychange', updateInstalledState)
    }
  }, [])

  useEffect(() => {
    latestDocumentRef.current = activeDocument
  }, [activeDocument])

  useEffect(() => {
    const boot = async () => {
      const [savedTheme, privacyDismissed, lastDocumentId] = await Promise.all([
        getSetting<ThemeMode>('theme', 'light'),
        getSetting<boolean>('privacyNoticeDismissed', false),
        getSetting<string | null>('lastDocumentId', null),
      ])

      setTheme(savedTheme)
      setPrivacyOpen(!privacyDismissed)

      if (lastDocumentId) {
        const documentRecord = await db.documents.get(lastDocumentId)
        if (documentRecord) {
          lastPersistedRef.current = {
            id: documentRecord.id,
            markdown: documentRecord.markdown,
            name: documentRecord.name,
          }
          setActiveDocument(documentRecord)
        }
      }

      setIsBooting(false)
    }

    void boot()
  }, [])

  useEffect(() => {
    if (!statusMessage) {
      return
    }

    const timer = window.setTimeout(() => setStatusMessage(null), 2600)
    return () => window.clearTimeout(timer)
  }, [statusMessage])

  useEffect(() => {
    return () => {
      if (selectedScreenshotUrl) {
        URL.revokeObjectURL(selectedScreenshotUrl)
      }
    }
  }, [selectedScreenshotUrl])

  const persistDocument = useCallback(
    async (documentToPersist: DocumentRecord | null, options?: { silent?: boolean }) => {
      if (!documentToPersist) {
        return
      }

      const persisted = lastPersistedRef.current
      const isUnchanged =
        persisted?.id === documentToPersist.id &&
        persisted.markdown === documentToPersist.markdown &&
        persisted.name === documentToPersist.name

      if (isUnchanged) {
        return
      }

      const updatedAt = Date.now()
      await db.documents.update(documentToPersist.id, {
        markdown: documentToPersist.markdown,
        name: documentToPersist.name,
        updatedAt,
      })

      lastPersistedRef.current = {
        id: documentToPersist.id,
        markdown: documentToPersist.markdown,
        name: documentToPersist.name,
      }

      setActiveDocument((current) => {
        if (!current || current.id !== documentToPersist.id) {
          return current
        }

        return {
          ...current,
          updatedAt,
        }
      })

      if (!options?.silent) {
        setStatusMessage('Draft saved on this device.')
      }
    },
    [],
  )

  useEffect(() => {
    if (viewMode !== 'edit' || !latestDocumentRef.current) {
      return
    }

    const interval = window.setInterval(() => {
      void persistDocument(latestDocumentRef.current, { silent: true })
    }, 5000)

    return () => window.clearInterval(interval)
  }, [activeDocument?.id, persistDocument, viewMode])

  async function handleDocumentOpen(documentId: string) {
    await persistDocument(latestDocumentRef.current, { silent: true })

    const documentRecord = await db.documents.get(documentId)
    if (!documentRecord) {
      return
    }

    lastPersistedRef.current = {
      id: documentRecord.id,
      markdown: documentRecord.markdown,
      name: documentRecord.name,
    }
    setActiveDocument(documentRecord)
    setViewMode('read')
    setActiveSidebarTab('history')
    await setSetting('lastDocumentId', documentRecord.id)
  }

  async function importMarkdown(
    rawMarkdown: string,
    proposedName: string,
    sourceType: DocumentRecord['sourceType'],
  ) {
    await persistDocument(latestDocumentRef.current, { silent: true })

    const markdown = normalizeMarkdown(rawMarkdown)
    if (!markdown.trim()) {
      setStatusMessage('Paste or drop a markdown file to begin.')
      return
    }

    const importSignature = await sha256(markdown)
    const existing = await db.documents.where('importSignature').equals(importSignature).first()

    if (existing) {
      await handleDocumentOpen(existing.id)
      setStatusMessage('Reopened your saved document.')
      return
    }

    const timestamp = Date.now()
    const nextDocument: DocumentRecord = {
      id: crypto.randomUUID(),
      name: deriveDocumentName(markdown, proposedName),
      markdown,
      importSignature,
      sourceType,
      createdAt: timestamp,
      updatedAt: timestamp,
    }

    await db.documents.put(nextDocument)
    lastPersistedRef.current = {
      id: nextDocument.id,
      markdown: nextDocument.markdown,
      name: nextDocument.name,
    }
    setActiveDocument(nextDocument)
    setViewMode('read')
    setImportSheetOpen(false)
    await setSetting('lastDocumentId', nextDocument.id)
    setStatusMessage('Markdown imported and saved on this device.')
  }

  async function importFile(file: File) {
    if (!isMarkdownFile(file)) {
      setStatusMessage('MarkView only imports markdown text files right now.')
      return
    }

    const markdown = await file.text()
    await importMarkdown(markdown, file.name.replace(/\.(md|markdown|txt)$/i, ''), 'file')
  }

  const importMarkdownFromEffect = useEffectEvent(importMarkdown)
  const importFileFromEffect = useEffectEvent(importFile)

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable
      ) {
        return
      }

      if (viewMode === 'edit') {
        return
      }

      const file = event.clipboardData?.files?.[0]
      if (file && isMarkdownFile(file)) {
        event.preventDefault()
        void importFileFromEffect(file)
        return
      }

      if (!activeDocument || importSheetOpen) {
        const text = event.clipboardData?.getData('text/plain')?.trim()
        if (text) {
          event.preventDefault()
          void importMarkdownFromEffect(text, 'Pasted Markdown', 'paste')
          setImportSheetOpen(false)
        }
      }
    }

    window.addEventListener('paste', handlePaste)
    return () => window.removeEventListener('paste', handlePaste)
  }, [activeDocument, importSheetOpen, viewMode])

  useEffect(() => {
    const handleDragOver = (event: DragEvent) => {
      if (event.dataTransfer?.types.includes('Files')) {
        event.preventDefault()
        setDragActive(true)
      }
    }

    const handleDragLeave = (event: DragEvent) => {
      if (event.relatedTarget === null) {
        setDragActive(false)
      }
    }

    const handleDrop = (event: DragEvent) => {
      event.preventDefault()
      setDragActive(false)

      const file = event.dataTransfer?.files?.[0]
      if (file) {
        void importFileFromEffect(file)
        return
      }

      const text = event.dataTransfer?.getData('text/plain')?.trim()
      if (text) {
        void importMarkdownFromEffect(text, 'Dropped Markdown', 'paste')
      }
    }

    window.addEventListener('dragover', handleDragOver)
    window.addEventListener('dragleave', handleDragLeave)
    window.addEventListener('drop', handleDrop)

    return () => {
      window.removeEventListener('dragover', handleDragOver)
      window.removeEventListener('dragleave', handleDragLeave)
      window.removeEventListener('drop', handleDrop)
    }
  }, [])

  const handleThemeToggle = async () => {
    const nextTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(nextTheme)
    await setSetting('theme', nextTheme)
  }

  const handleInstallClick = async () => {
    if (installPromptEvent) {
      try {
        await installPromptEvent.prompt()
        const choice = await installPromptEvent.userChoice
        if (choice.outcome === 'accepted') {
          setStatusMessage('Install started.')
        } else {
          setStatusMessage('Install canceled.')
        }
      } catch {
        setStatusMessage('Install prompt failed.')
      } finally {
        setInstallPromptEvent(null)
      }
      return
    }

    if (isIOS && !isInstalled) {
      setInstallHelpOpen(true)
    }
  }

  const handlePrivacyDismiss = async () => {
    setPrivacyOpen(false)
    await setSetting('privacyNoticeDismissed', true)
  }

  const handleMarkdownChange = (markdown: string) => {
    setActiveDocument((current) => {
      if (!current) {
        return current
      }

      return {
        ...current,
        markdown,
        name: deriveDocumentName(markdown, current.name),
      }
    })
  }

  const handleCaptureScreenshot = async () => {
    if (!activeDocument || !readerViewportRef.current || isCapturing || viewMode !== 'read') {
      return
    }

    setIsCapturing(true)
    const target = readerViewportRef.current
    const rootStyles = getComputedStyle(document.documentElement)
    const solidBackground =
      rootStyles.getPropertyValue('--reader-surface').trim() || '#fffaf3'
    const panelBackground = rootStyles.getPropertyValue('--panel-surface').trim() || '#fffaf3'
    const editorBackground = rootStyles.getPropertyValue('--editor-surface').trim() || '#fffefb'
    const textColor = rootStyles.getPropertyValue('--text-primary').trim() || '#231b17'
    const softTextColor = rootStyles.getPropertyValue('--text-soft').trim() || '#716459'
    const accentColor = rootStyles.getPropertyValue('--accent').trim() || '#d66b38'
    const accentInk = rootStyles.getPropertyValue('--accent-ink').trim() || '#8f451d'
    const borderColor =
      rootStyles.getPropertyValue('--border-subtle').trim() || 'rgba(62, 46, 34, 0.12)'

    try {
      const { default: html2canvas } = await import('html2canvas')
      const canvas = await html2canvas(target, {
        backgroundColor: solidBackground,
        width: target.clientWidth,
        height: target.clientHeight,
        windowWidth: target.clientWidth,
        windowHeight: target.clientHeight,
        scale: Math.min(window.devicePixelRatio || 1, 2),
        useCORS: false,
        logging: false,
        onclone: (clonedDocument) => {
          clonedDocument.documentElement.dataset.capture = 'true'
          clonedDocument.documentElement.style.background = solidBackground
          clonedDocument.body.style.background = solidBackground
          clonedDocument.body.style.color = textColor

          clonedDocument
            .querySelectorAll('.status-toast, .dialog-backdrop, .sidebar-backdrop')
            .forEach((element) => element.remove())

          const clonedTarget = clonedDocument.getElementById(captureTargetId)
          if (clonedTarget instanceof HTMLElement) {
            clonedTarget.scrollTop = target.scrollTop
            clonedTarget.style.overflow = 'hidden'
            clonedTarget.style.background = solidBackground
            clonedTarget.style.backgroundImage = 'none'

            clonedTarget.querySelectorAll<HTMLElement>('*').forEach((element) => {
              element.style.boxShadow = 'none'
              element.style.textShadow = 'none'
            })

            clonedTarget
              .querySelectorAll<HTMLElement>(
                '.reader-card, .reader-code-block, .image-placeholder, .image-placeholder-box',
              )
              .forEach((element) => {
                element.style.background = solidBackground
                element.style.backgroundImage = 'none'
                element.style.borderColor = borderColor
              })

            clonedTarget.querySelectorAll<HTMLElement>('.reader-inline-code').forEach((element) => {
              element.style.background = editorBackground
              element.style.color = textColor
            })

            clonedTarget.querySelectorAll<HTMLElement>('.reader-link').forEach((element) => {
              element.style.color = accentInk
              element.style.textDecorationColor = accentColor
            })

            clonedTarget
              .querySelectorAll<HTMLElement>('.reader-blockquote, figcaption, th')
              .forEach((element) => {
                element.style.color = softTextColor
              })

            clonedTarget.querySelectorAll<HTMLElement>('.reader-blockquote').forEach((element) => {
              element.style.borderLeftColor = accentColor
            })

            clonedTarget.querySelectorAll<HTMLElement>('table').forEach((element) => {
              element.style.background = panelBackground
              element.style.borderColor = borderColor
            })

            clonedTarget.querySelectorAll<HTMLElement>('hr, td, th').forEach((element) => {
              element.style.borderColor = borderColor
            })
          }
        },
      })

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png', 0.96)
      })

      if (!blob) {
        setStatusMessage('Save Screen failed.')
        return
      }

      await db.screenshots.put({
        id: crypto.randomUUID(),
        documentId: activeDocument.id,
        blob,
        width: canvas.width,
        height: canvas.height,
        scrollTop: target.scrollTop,
        createdAt: Date.now(),
      })

      setActiveSidebarTab('saved')
      setPanelOpen(true)
      setStatusMessage('Screen saved to this device.')
    } catch (error) {
      console.error('Save Screen failed', error)
      setStatusMessage('Save Screen failed. Check the console for details.')
    } finally {
      setIsCapturing(false)
    }
  }

  const handleDeleteScreenshot = async (screenshotId: string) => {
    await db.screenshots.delete(screenshotId)
    setSelectedScreenshot((current) => (current?.id === screenshotId ? null : current))
    setStatusMessage('Saved screen removed.')
  }

  const handleFilePick = () => {
    fileInputRef.current?.click()
  }

  const handleReplaceDocument = async () => {
    await persistDocument(latestDocumentRef.current, { silent: true })
    setImportSheetOpen(true)
  }

  const handleViewModeToggle = async () => {
    if (viewMode === 'edit') {
      await persistDocument(latestDocumentRef.current, { silent: true })
      setViewMode('read')
      return
    }

    setViewMode('edit')
  }

  const handleFileInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (file) {
      await importFile(file)
    }
  }

  if (isBooting) {
    return <div className="boot-screen">Preparing your offline reading space...</div>
  }

  const currentDocument = activeDocument
  const hasDocument = Boolean(activeDocument)
  const showInstallButton = !isInstalled && (Boolean(installPromptEvent) || isIOS)

  return (
    <div className="app-shell">
      <input
        ref={fileInputRef}
        type="file"
        accept=".md,.markdown,.txt,text/markdown,text/plain"
        className="sr-only"
        onChange={handleFileInputChange}
      />

      <header className="topbar">
        <div className="brand-lockup">
          <img className="brand-mark" src="/favicon.svg" alt="" aria-hidden="true" />
          <div>
            <p className="eyebrow">MarkView Reader</p>
            <h1>{activeDocument?.name ?? 'MarkView'}</h1>
          </div>
        </div>

        <div className="topbar-actions">
          <button
            type="button"
            className={`ghost-button icon-button ${activeSidebarTab === 'history' && panelOpen ? 'is-active' : ''}`}
            onClick={() => {
              setActiveSidebarTab('history')
              setPanelOpen((current) => (activeSidebarTab === 'history' ? !current : true))
            }}
            disabled={historyDocuments.length === 0}
            aria-label="History"
            title="History"
          >
            <HistoryIcon />
          </button>
          <button type="button" className="ghost-button" onClick={() => void handleReplaceDocument()}>
            {hasDocument ? 'Open another' : 'Open markdown'}
          </button>
          <button
            type="button"
            className="ghost-button"
            onClick={() => {
              setActiveSidebarTab('saved')
              setPanelOpen((current) => (activeSidebarTab === 'saved' ? !current : true))
            }}
            disabled={!hasDocument}
          >
            Saved
            <span className="count-pill">{screenshots.length}</span>
          </button>
          <button
            type="button"
            className="ghost-button"
            onClick={() => void handleViewModeToggle()}
            disabled={!hasDocument}
          >
            {viewMode === 'read' ? 'Edit' : 'Read'}
          </button>
          <button
            type="button"
            className="ghost-button"
            onClick={handleCaptureScreenshot}
            disabled={!hasDocument || viewMode !== 'read' || isCapturing}
          >
            {isCapturing ? 'Saving...' : 'Save Screen'}
          </button>
          <button type="button" className="theme-button" onClick={handleThemeToggle}>
            {theme === 'light' ? 'Dark' : 'Light'}
          </button>
          {showInstallButton ? (
            <button
              type="button"
              className="ghost-button icon-button install-button"
              onClick={() => void handleInstallClick()}
              aria-label={installPromptEvent ? 'Install app' : 'How to install'}
              title={installPromptEvent ? 'Install app' : 'How to install'}
            >
              <InstallIcon />
            </button>
          ) : null}
        </div>
      </header>

      <div className={`drag-curtain ${dragActive ? 'is-active' : ''}`}>
        <div className="drag-card">
          <p>Drop a markdown file to open it locally.</p>
        </div>
      </div>

      <div className="workspace">
        <main
          ref={readerViewportRef}
          id={captureTargetId}
          className={`reader-viewport ${hasDocument ? '' : 'is-empty'}`}
        >
          {!currentDocument ? (
            <section className="empty-state">
              <div className="empty-state-card">
                <p className="eyebrow">Typography first</p>
                <h2>Open a markdown file and let the reading breathe.</h2>
                <p>
                  Drag in a file, paste raw markdown, or choose one from your device. Every saved
                  screen stays in IndexedDB on this device.
                </p>
                <div className="empty-actions">
                  <button type="button" className="primary-button" onClick={handleFilePick}>
                    Choose a file
                  </button>
                  <button
                    type="button"
                    className="ghost-button strong"
                    onClick={() => setImportSheetOpen(true)}
                  >
                    Paste markdown
                  </button>
                </div>
              </div>
            </section>
          ) : viewMode === 'read' ? (
            <MarkdownSurface
              markdown={currentDocument.markdown}
              onExternalLinkAttempt={() => {
                setStatusMessage(
                  'External links stay disabled so the app never leaves your device.',
                )
              }}
            />
          ) : (
            <section className="editor-shell">
              <div className="editor-header">
                <div>
                  <p className="eyebrow">Edit mode</p>
                  <h2>{currentDocument.name}</h2>
                </div>
                <p className="autosave-note">Autosaves every 5 seconds while you edit.</p>
              </div>

              <label className="raw-editor-shell">
                <span className="sr-only">Markdown editor</span>
                <textarea
                  className="raw-editor"
                  value={currentDocument.markdown}
                  onChange={(event) => handleMarkdownChange(event.target.value)}
                />
              </label>
            </section>
          )}
        </main>

        <SidebarPanel
          activeDocumentId={activeDocument?.id ?? null}
          activeTab={activeSidebarTab}
          documents={historyDocuments}
          isOpen={panelOpen}
          screenshots={screenshots}
          onClose={() => setPanelOpen(false)}
          onDocumentOpen={async (documentId) => {
            await handleDocumentOpen(documentId)
            setPanelOpen(false)
          }}
          onDeleteScreenshot={handleDeleteScreenshot}
          onScreenshotOpen={setSelectedScreenshot}
          onTabChange={(tab) => {
            setActiveSidebarTab(tab)
            setPanelOpen(true)
          }}
        />
      </div>

      {importSheetOpen ? (
        <ImportSheet
          isOpen={importSheetOpen}
          onClose={() => setImportSheetOpen(false)}
          onFilePick={handleFilePick}
          onImportMarkdown={async (markdown, name) => {
            await importMarkdown(markdown, name, 'paste')
          }}
        />
      ) : null}

      <PrivacyNotice isOpen={privacyOpen} onDismiss={handlePrivacyDismiss} />

      {installHelpOpen ? (
        <div className="dialog-backdrop" role="presentation" onClick={() => setInstallHelpOpen(false)}>
          <section
            className="install-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="install-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="eyebrow">Install</p>
            <h2 id="install-title">Add MarkView to your Home Screen</h2>
            {isSafari ? (
              <ol className="install-steps">
                <li>Tap the Share button in Safari.</li>
                <li>Scroll down and choose Add to Home Screen.</li>
                <li>Tap Add to install MarkView as its own app.</li>
              </ol>
            ) : (
              <ol className="install-steps">
                <li>Open this page in Safari.</li>
                <li>Tap the Share button.</li>
                <li>Choose Add to Home Screen, then tap Add.</li>
              </ol>
            )}
            <div className="install-actions">
              <button
                type="button"
                className="primary-button"
                onClick={() => setInstallHelpOpen(false)}
              >
                Got it
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {selectedScreenshot && selectedScreenshotUrl ? (
        <div
          className="dialog-backdrop"
          role="presentation"
          onClick={() => setSelectedScreenshot(null)}
        >
          <div
            className="screenshot-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Saved screen preview"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="dialog-close"
              onClick={() => setSelectedScreenshot(null)}
            >
              Close
            </button>
            <img
              src={selectedScreenshotUrl}
              alt={`Saved screen from ${new Date(selectedScreenshot.createdAt).toLocaleString()}`}
            />
          </div>
        </div>
      ) : null}

      {statusMessage ? <div className="status-toast">{statusMessage}</div> : null}
    </div>
  )
}

export default App
